// ============================================================
// SplashScreen — BH Studios opening (standardized across BH apps)
// Logic mirrors BH-Productivity/src/components/layout/SplashScreen.tsx.
// Word-dogs uses vanilla CSS instead of Tailwind, so utility
// classes are expressed as inline styles — the behavior is identical.
// ============================================================

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface SplashScreenProps {
  onComplete: () => void;
}

export default function SplashScreen({ onComplete }: SplashScreenProps) {
  const [canSkip, setCanSkip] = useState(false);
  const [exiting, setExiting] = useState(false);

  const handleStart = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(() => onComplete(), 600);
  };

  useEffect(() => {
    const timer = setTimeout(() => setCanSkip(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            background: '#f5f5f0',
          }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src="/bh-studios.png"
              alt="BH Studios"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            {canSkip && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                onClick={handleStart}
                style={{
                  position: 'absolute',
                  bottom: '6rem',
                  padding: '0.75rem 2rem',
                  background: '#1f2937',
                  color: '#ffffff',
                  fontSize: '1.125rem',
                  fontWeight: 500,
                  border: 'none',
                  borderRadius: '9999px',
                  cursor: 'pointer',
                  transition: 'transform 0.1s',
                }}
                onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.95)')}
                onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                onTouchStart={(e) => (e.currentTarget.style.transform = 'scale(0.95)')}
                onTouchEnd={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                Start
              </motion.button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
